import axios from '@/plugins/axios'
import moment from 'moment'
import * as actionTypes from '@/store/action-types'
import * as getterTypes from '@/store/getter-types'
import * as mutationTypes from '@/store/mutation-types'
import { CompanyStructure, GlobalState, LanguageRow } from '@/types'
import {
  CompanyInsertedCalendarEventResponse,
  CompanyStructureCalendarEventResponse, CompanyStructureForPlanningResponse,
  DepartmentEmployeeResponse
} from '@/types/service-response'
import {
  CalendarEventPayload,
  CompanyInsertEventPayload, CompanyInsertPlanningGroupPayload, CreatePlanningGroup,
  DepartmentEmployeeListPayload
} from '@/types/payload'
import { CompanyEmployee } from '@/model/company/CompanyEmployee'
import { CalendarEvent } from '@/model/planning/CalendarEvent'
import { CompanyStructureState } from '@/types/application-state'
import { Employee } from '@/model/companyStructure/Employee'
import { PlanningGroup } from '@/model/companyStructure/PlanningGroup'
import { ProfitCenter } from '@/model/companyStructure/ProfitCenter'
import { Department } from '@/model/companyStructure/Department'
import { EventList } from '@/model/planning/EventList'
import { PlanningGroupsSettings } from '@/model/settings/PlanningGroupsSettings'
import { PlanningGroupsList } from '@/model/settings/PlanningGroupsList'
import { School } from '@/model/companyStructure/School'
import { Holiday } from '@/model/companyStructure/Holiday'

const state: CompanyStructureState = {
  calendarEvents: [],
  departments: [],
  employees: [],
  planningGroups: [],
  profitCenters: [],
  companyStructureInitialized: false,
  schoolList: [],
  holidayList: []
}

const getters = {
  [getterTypes.COMPANY_GET_PROFIT_CENTER_CALENDAR_EVENTS] (state: CompanyStructureState): Array<CalendarEvent> {
    return state.calendarEvents
  },

  [getterTypes.COMPANY_GET_INITIALIZED_STATE] (state: CompanyStructureState): boolean {
    return state.companyStructureInitialized
  },

  [getterTypes.COMPANY_GET_BASIC_COMPANY_STRUCTURE] (state: CompanyStructureState): CompanyStructure {
    return {
      profitCenterList: state.profitCenters,
      departmentList: state.departments,
      planningGroupList: state.planningGroups,
      employeeList: state.employees
    }
  },

  [getterTypes.COMPANY_GET_COMPANY_STRUCTURE_SCHOOL_LIST] (state: CompanyStructureState): Array<School> {
    return state.schoolList
  },

  [getterTypes.COMPANY_GET_COMPANY_STRUCTURE_HOLIDAY_LIST] (state: CompanyStructureState): Array<Holiday> {
    return state.holidayList
  },
}

const mutations = {
  [mutationTypes.COMPANY_FETCH_PROFIT_CENTER_CALENDAR_EVENTS_MUTATION] (state: CompanyStructureState, payload: Array<CalendarEvent>): void {
    state.calendarEvents = payload
  },

  [mutationTypes.COMPANY_FETCH_BASIC_COMPANY_STRUCTURE_MUTATION] (state: CompanyStructureState, payload: CompanyStructureForPlanningResponse): void {
    state.profitCenters = ProfitCenter.createFromResponse(payload)
    state.planningGroups = PlanningGroup.createFromResponse(payload)
    state.employees = Employee.createFromResponse(payload)
    state.departments = Department.createFromResponse(payload)
    state.schoolList = School.createFromResponse(payload)
    state.holidayList = Holiday.createFromResponse(payload)
  },
}

const actions = {
  async [actionTypes.COMPANY_FETCH_COMPANY_STRUCTURE] (
    { commit, rootState, state }: { commit: any, rootState: GlobalState, state: CompanyStructureState }): Promise<any> {
    // const endpoint = process.env.VUE_APP_API_VERSION === 'old' ? '/api/CompanyStruture' : '/api/CompanyStructure'

    const response = await axios.get('/api/CompanyStructure', {
      params: {
        'ID': rootState.currentUser!.id,
        'sprache': rootState.currentUser!.uiLanguage.syzId
      }
    })

    state.companyStructureInitialized = true
    const data: CompanyStructureForPlanningResponse = response.data
    commit(mutationTypes.COMPANY_FETCH_BASIC_COMPANY_STRUCTURE_MUTATION, data)
    return data
  },

  async [actionTypes.COMPANY_FETCH_DEPARTMENT_EMPLOYEE_LIST] ({ rootState }: { rootState: GlobalState }, payload: DepartmentEmployeeListPayload): Promise<Array<CompanyEmployee>> {
    const response = await axios.get('/api/CompanyStructure', {
      params: {
        'abt': payload.departmentId,
        'date': moment().format('YYYY-MM-DD'),
        'sprache': rootState.currentUser!.uiLanguage.syzId,
      }
    })

    const data: DepartmentEmployeeResponse = response.data
    return CompanyEmployee.createListFromDepartmentEmplyeeListResponse(data)
  },

  async [actionTypes.COMPANY_FETCH_COMPANY_EMPLOYEE_LIST] ({ rootState }: { rootState: GlobalState }, payload: any): Promise<any> {
    const response = await axios.get('/api/CompanyStructure', {
      params: {
        component: '',
        mitarbeiter: payload.employeeId,
        Sprache: rootState.selectedUiLanguage!.syzId
      }
    })

    return response.data
  },

  // syz_GrabKalenderArtProfitCenter [404] -- WORKING
  async [actionTypes.COMPANY_FETCH_PROFIT_CENTER_CALENDAR_EVENTS] ({ rootState, commit }: { rootState: GlobalState, commit: any }, payload: any) {
    const response = await axios.get('/api/CompanyStructure', {
      params: {
        'sprache': rootState.selectedUiLanguage!.syzId,
        'calendarEventsForProfitCenter': payload.profitCenterId,
      }
    })
    const data: CompanyStructureCalendarEventResponse = response.data
    return CalendarEvent.createListFromResponse(data)
  },

  async [actionTypes.FETCH_EVENT_LIST] ({ rootState, commit }: { rootState: GlobalState, commit: any }) {
    const response = await axios.get('/api/CompanyStructure', {
      params: {
        'sprache': rootState.selectedUiLanguage!.syzId,
        'listAllCalendarEvents': '',
      }
    })
    const data: Array<CompanyInsertedCalendarEventResponse> = response.data
    return EventList.createListFromResponse(data)
  },

  // syz_InsertKalenderArt [404] -- WORKING
  async [actionTypes.COMPANY_INSERT_CALENDAR_EVENT] ({ rootState }: { rootState: GlobalState }, payload: CompanyInsertEventPayload) {
    const ceNameGer = payload.languageRows.find((item: LanguageRow) => item.language === 'de')
    const ceNameFra = payload.languageRows.find((item: LanguageRow) => item.language === 'fr')
    const ceNameIta = payload.languageRows.find((item: LanguageRow) => item.language === 'it')
    const ceNameEng = payload.languageRows.find((item: LanguageRow) => item.language === 'en')
    const ceNamePor = payload.languageRows.find((item: LanguageRow) => item.language === 'pt')

    const response = await axios.post('/api/CompanyStructure', null, {
      params: {
        ceNameGer: ceNameGer ? ceNameGer.description : '',
        ceNameFra: ceNameFra ? ceNameFra.description : '',
        ceNameIta: ceNameIta ? ceNameIta.description : '',
        ceNameEng: ceNameEng ? ceNameEng.description : '',
        ceNamePor: ceNamePor ? ceNamePor.description : ''
      }
    })
    return response
  },

  // syz_InsertUpdateKalenderArtProfitCenter [404] -- WORKING
  async [actionTypes.COMPANY_UPDATE_DELETE_CALENDAR_EVENT] ({ rootState }: { rootState: GlobalState }, payload: CalendarEventPayload): Promise<boolean> {
    const response = await axios.post('/api/CompanyStructure', null, {
      params: {
        action: payload.action,
        profitCenterId: payload.profitCenterId,
        calendarEventId: payload.eventId,
        color: payload.color,
        isPlanable: payload.isPlannable,
        ID: payload.id ? payload.id : '',
        sprache: payload.language ? payload.language : rootState.currentUser!.uiLanguage.syzId
      }
    })
    if (response.data) {
      return true
    } else {
      throw 'error'
    }
  },

  // PlanningGroup fetch by department
  async [actionTypes.COMPANY_FETCH_DEPARTMENT_PLANNING_GROUPS] ({ rootState, commit }: { rootState: GlobalState, commit: any }, payload: any) {
    const response = await axios.post('/api/Planungsgruppe', null, {
      params: {
        'AbtID': payload.departmentId,
        'plgID': '',
        'date': '',
        'from': ''
      }
    })
    const data: any = response.data
    return PlanningGroupsSettings.createListFromResponse(data)
  },

  // PlanningGroup fetch
  async [actionTypes.FETCH_PLANNING_GROUP_LIST] ({ rootState, commit }: { rootState: GlobalState, commit: any }) {
    const response = await axios.post('/api/Planungsgruppe?GrabPlanungsgruppe', null, {})

    return PlanningGroupsList.createListFromResponse(response.data)
  },

  // PlanningGroup create
  async [actionTypes.COMPANY_INSERT_PLANNING_GROUP] ({ rootState }: { rootState: GlobalState }, payload: CompanyInsertPlanningGroupPayload) {
    const pgNameGer = payload.languageRows.find((item: LanguageRow) => item.language === 'de')
    const pgNameFra = payload.languageRows.find((item: LanguageRow) => item.language === 'fr')
    const pgNameIta = payload.languageRows.find((item: LanguageRow) => item.language === 'it')
    const pgNameEng = payload.languageRows.find((item: LanguageRow) => item.language === 'en')
    const pgNamePor = payload.languageRows.find((item: LanguageRow) => item.language === 'pt')

    const response = await axios.post('/api/Planungsgruppe', null, {
      params: {
        txtPLGGer: pgNameGer ? pgNameGer.description : '',
        txtPLGFra: pgNameFra ? pgNameFra.description : '',
        txtPLGIta: pgNameIta ? pgNameIta.description : '',
        txtPLGEng: pgNameEng ? pgNameEng.description : '',
        txtPLGPor: pgNamePor ? pgNamePor.description : ''
      }
    })
    return response
  },

  // PlanningGroup edit/delete
  async [actionTypes.COMPANY_UPDATE_DELETE_PLANNING_GROUP] ({ rootState }: { rootState: GlobalState }, payload: CreatePlanningGroup): Promise<boolean> {
    const response = await axios.post('/api/Planungsgruppe', null, {
      params: {
        action: payload.action,
        abt: payload.departmentId,
        plg: payload.planningGroupId,
        color: payload.color,
        ID: payload.id ? payload.id : '',
      }
    })
    if (response.data) {
      return true
    } else {
      throw 'error'
    }
  },
}

const companyModule = {
  state,
  getters,
  mutations,
  actions
}

export default companyModule
